import { apiClient } from '@/api/client'
import {
  REPORTS_COMPLIANCE,
  REPORTS_COMPLIANCE_EXPORT_CSV,
  REPORTS_COMPLIANCE_EXPORT_EXCEL,
  REPORTS_COMPLIANCE_SUMMARY,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type {
  ComplianceReportQuery,
  ComplianceReportsListResponse,
  ComplianceReportSummaryFilters,
  ComplianceReportSummaryResponse,
} from '@/features/reports/types/compliance-report.types'

function serializeCommonFilters(
  filters?: ComplianceReportQuery | ComplianceReportSummaryFilters
): Record<string, unknown> {
  const params: Record<string, unknown> = {}
  if (!filters) return params

  if (filters.companyIds && filters.companyIds.length > 0) {
    params.companyIds = filters.companyIds.join(',')
  }
  if (filters.search) params.search = filters.search
  if (filters.companyStatus) params.companyStatus = filters.companyStatus
  if (filters.reportStatus) params.reportStatus = filters.reportStatus
  if (filters.hasOpenIssues != null) params.hasOpenIssues = filters.hasOpenIssues
  if (filters.dateFrom) params.dateFrom = filters.dateFrom
  if (filters.dateTo) params.dateTo = filters.dateTo
  if (filters.minComplianceScore != null) {
    params.minComplianceScore = filters.minComplianceScore
  }
  if (filters.maxComplianceScore != null) {
    params.maxComplianceScore = filters.maxComplianceScore
  }

  return params
}

function buildListParams(query?: ComplianceReportQuery): Record<string, unknown> {
  const params = serializeCommonFilters(query)
  if (!query) return params
  if (query.sortBy) params.sortBy = query.sortBy
  if (query.sortOrder) params.sortOrder = query.sortOrder
  if (query.page != null) params.page = query.page
  if (query.limit != null) params.limit = query.limit
  return params
}

function toListResponse(
  payload:
    | {
        data?: ComplianceReportsListResponse['data']
        total?: number
        page?: number
        limit?: number
        summary?: ComplianceReportsListResponse['summary']
        serverTime?: string
      }
    | undefined,
  query?: ComplianceReportQuery
): ComplianceReportsListResponse {
  const total = Number(payload?.total) || 0
  const limit = Number(payload?.limit) || query?.limit || 10
  const page = Number(payload?.page) || query?.page || 1

  return {
    data: Array.isArray(payload?.data) ? payload.data : [],
    total,
    page,
    limit,
    totalPages: Math.ceil(total / limit) || 0,
    summary: {
      totalCompanies: Number(payload?.summary?.totalCompanies) || 0,
      compliantCompanies: Number(payload?.summary?.compliantCompanies) || 0,
      needsAttentionCompanies: Number(payload?.summary?.needsAttentionCompanies) || 0,
      atRiskCompanies: Number(payload?.summary?.atRiskCompanies) || 0,
      averageComplianceScore: Number(payload?.summary?.averageComplianceScore) || 0,
      totalOpenIssues: Number(payload?.summary?.totalOpenIssues) || 0,
      activeMembers: Number(payload?.summary?.activeMembers) || 0,
      pendingApprovals: Number(payload?.summary?.pendingApprovals) || 0,
      payrollRunsWithErrors: Number(payload?.summary?.payrollRunsWithErrors) || 0,
      auditEvents: Number(payload?.summary?.auditEvents) || 0,
      // Backend summary is expected to return distinct logical document counts.
      documents: Number(payload?.summary?.documents) || 0,
    },
    serverTime: payload?.serverTime ?? new Date().toISOString(),
  }
}

export function getComplianceReportSummaryFilters(
  query?: ComplianceReportQuery
): ComplianceReportSummaryFilters | undefined {
  if (!query) return undefined

  return {
    companyIds: query.companyIds,
    search: query.search,
    companyStatus: query.companyStatus,
    reportStatus: query.reportStatus,
    hasOpenIssues: query.hasOpenIssues,
    dateFrom: query.dateFrom,
    dateTo: query.dateTo,
    minComplianceScore: query.minComplianceScore,
    maxComplianceScore: query.maxComplianceScore,
  }
}

export async function getComplianceReports(
  query?: ComplianceReportQuery
): Promise<ComplianceReportsListResponse> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<{
      data: ComplianceReportsListResponse['data']
      total: number
      page: number
      limit: number
      summary: ComplianceReportsListResponse['summary']
      serverTime: string
    }>
  >(REPORTS_COMPLIANCE, {
    params: buildListParams(query),
  })

  return toListResponse(data?.data, query)
}

export async function getComplianceReportSummary(
  filters?: ComplianceReportSummaryFilters
): Promise<ComplianceReportSummaryResponse> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<{
      data: ComplianceReportSummaryResponse['data']
      serverTime: string
    }>
  >(REPORTS_COMPLIANCE_SUMMARY, {
    params: serializeCommonFilters(filters),
  })

  const payload = data?.data
  return {
    data: {
      totalCompanies: Number(payload?.data?.totalCompanies) || 0,
      compliantCompanies: Number(payload?.data?.compliantCompanies) || 0,
      needsAttentionCompanies: Number(payload?.data?.needsAttentionCompanies) || 0,
      atRiskCompanies: Number(payload?.data?.atRiskCompanies) || 0,
      averageComplianceScore: Number(payload?.data?.averageComplianceScore) || 0,
      totalOpenIssues: Number(payload?.data?.totalOpenIssues) || 0,
      activeMembers: Number(payload?.data?.activeMembers) || 0,
      pendingApprovals: Number(payload?.data?.pendingApprovals) || 0,
      payrollRunsWithErrors: Number(payload?.data?.payrollRunsWithErrors) || 0,
      auditEvents: Number(payload?.data?.auditEvents) || 0,
      // Backend summary is expected to return distinct logical document counts.
      documents: Number(payload?.data?.documents) || 0,
    },
    serverTime: payload?.serverTime ?? new Date().toISOString(),
  }
}

export async function exportComplianceReportsCsv(
  filters?: ComplianceReportSummaryFilters
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(REPORTS_COMPLIANCE_EXPORT_CSV, {
    params: serializeCommonFilters(filters),
    responseType: 'blob',
  })
  return data
}

export async function exportComplianceReportsExcel(
  filters?: ComplianceReportSummaryFilters
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(REPORTS_COMPLIANCE_EXPORT_EXCEL, {
    params: serializeCommonFilters(filters),
    responseType: 'blob',
  })
  return data
}
